/**
 * Slice-local typed message keys for the assistant-connection surface.
 *
 * The central `messages.ts` is owned by another slice, so this module follows
 * the same typed-key pattern locally (the WhatsApp wizard set the precedent).
 * Copy stays in the repository's English idiom.
 *
 * Every connection recipe below was verified on 2026-07-28 against the client's
 * own documentation or its installed binary; the sources are recorded in
 * `deliverables/Obsina_MCP_Server_Uygulama_Plani_2026-07-28.md`. Nothing here
 * claims the endpoint is reachable — that is read from deployment
 * configuration at render time (see `mcp-endpoint.ts`).
 */

export const MCP_SERVER_LABEL = "obsina" as const;

export type McpClientId =
  "claude-code" | "chatgpt" | "gemini-cli" | "codex-cli";

export interface McpClientRecipe {
  readonly id: McpClientId;
  /** Tab label — the client's own product name. */
  readonly tab: string;
  /** Shell commands in run order; empty when the client is configured in a UI. */
  readonly commands: (endpoint: string) => readonly string[];
  /** Short steps, at most three, in the order a person performs them. */
  readonly steps: readonly string[];
}

export const mcpClientRecipes: readonly McpClientRecipe[] = [
  {
    id: "claude-code",
    tab: "Claude Code",
    commands: (endpoint) => [
      `claude mcp add --transport http ${MCP_SERVER_LABEL} ${endpoint}`,
    ],
    steps: [
      "Komutu bir terminalde çalıştırın.",
      "Claude Code'u başlatın, /mcp yazın ve açılan tarayıcı sekmesinde Obsina'ya giriş yapın.",
    ],
  },
  {
    id: "chatgpt",
    tab: "ChatGPT",
    commands: () => [],
    steps: [
      "Web'deki ChatGPT'de Ayarlar'ı, sonra Güvenlik ve oturum açma'yı açın ve Geliştirici modunu etkinleştirin.",
      "Eklentiler'i açın, artı düğmesini seçin ve yukarıdaki sunucu adresini yapıştırın.",
      "ChatGPT istediğinde Obsina'ya giriş yapın.",
    ],
  },
  {
    id: "gemini-cli",
    tab: "Gemini CLI",
    commands: (endpoint) => [
      `gemini mcp add --transport http ${MCP_SERVER_LABEL} ${endpoint}`,
    ],
    steps: [
      "Komutu bir terminalde çalıştırın.",
      "Gemini CLI'ı başlatın; ilk bağlantıda giriş sayfasını Obsina'dan ister.",
    ],
  },
  {
    id: "codex-cli",
    tab: "Codex CLI",
    commands: (endpoint) => [
      `codex mcp add ${MCP_SERVER_LABEL} --url ${endpoint}`,
      `codex mcp login ${MCP_SERVER_LABEL}`,
    ],
    steps: [
      "Her iki komutu bir terminalde çalıştırın.",
      "İkinci komutun açtığı tarayıcı sekmesinde Obsina girişini tamamlayın.",
    ],
  },
] as const;

export const integrationsMessages = {
  title: "Asistan bağla",
  lede: "Şirket hafızanıza, zaten kullandığınız asistandan sorun.",
  exposes: {
    title: "Bir asistan Obsina'dan ne isteyebilir",
    tools: [
      {
        name: "search",
        body: "Sorduğunuz şeyle eşleşen öğeleri bulur.",
      },
      {
        name: "fetch",
        body: "Bir iletinin ya da belgenin tam metnini getirir.",
      },
      {
        // Adı da açıklaması da 2026-08-03'te değişti: araç artık cevap
        // üretmiyor, cevabın dayanacağı yetkili kanıtı döndürüyor. "Yanıtlar"
        // demek, ürünün yapmadığı bir şeyi vaat etmek olurdu.
        name: "obsina_find_evidence",
        body: "Bir sorunun dayandığı yetkili kanıtı getirir; cevabı asistanın kendi modeli yazar.",
      },
    ],
    scopeNote:
      "Üçü de yalnızca okur. Obsina sizin adınıza ileti göndermez, işlem yapmaz; asistan yalnızca sizin görmeye yetkili olduğunuzu görür.",
  },
  connect: {
    title: "Asistanınıza bağlayın",
    tablistLabel: "Asistan",
    endpointLabel: "Sunucu adresi",
    endpointPlaceholder: "https://…/mcp",
    copyEndpoint: "Adresi kopyala",
    copiedEndpoint: "Adres kopyalandı",
    copyCommand: "Komutu kopyala",
    copiedCommand: "Komut kopyalandı",
    copyFailed: "Kopyalanamadı — metni seçip kendiniz kopyalayın.",
    noInstallLink:
      "Bu asistanların hiçbiri özel sunucu için tek tıkla kurulum bağlantısı kabul etmiyor; bu yüzden her yol tek komut ya da tek ayar.",
  },
  unavailable: {
    tag: "Yayımlanmadı",
    // The tag beside the address already says the endpoint is not published,
    // so this sentence does not say it a second time; it adds the part the tag
    // cannot carry — why, and what stays usable.
    body: "Bu kurulum asistan uç noktası olmadan derlendi; adımlar bağlamanın ne gerektireceğini göstermek için görünür kalır.",
  },
} as const;

export type IntegrationsMessages = typeof integrationsMessages;

/**
 * The consent screen: one decision, stated once.
 *
 * The heading carries the assistant's name, so no line below it repeats which
 * assistant is asking. Read-only is said once, in the lede, rather than beside
 * each capability. Every outcome is words, never colour alone.
 */
export const consentMessages = {
  /** Shown while the request is being checked with the server. */
  loading: "Bu bağlantı isteği denetleniyor…",
  headingFor: (client: string) => `${client} asistanını Obsina'ya bağla`,
  lede: "Şirket hafızanızı sizin adınıza okuyacak — yalnızca görmeye zaten yetkili olduğunuzu. Hiçbir şey yazamaz, silemez, gönderemez.",
  organisationLabel: "Kurum",
  organisationUnknown: "Kurumunuz",
  roleLabel: "Rolünüz",
  roleOwner: "sahip",
  roleMember: "üye",
  allowsLabel: "Neler yapabilecek",
  scopes: {
    "obsina.read":
      "Zaten görebildiğiniz ileti, belge ve dosyalarda arama yapar ve onları açar.",
    "obsina.ask":
      "Bu malzemeden bir soruyu yanıtlar ve yanıtın arkasındaki kanıtı gösterir.",
  } as Record<string, string>,
  /**
   * Said beside the button that grants it, because it is the only fact here the
   * assistant did not choose for itself. Registration is open: the name in the
   * heading is whatever was typed at registration time, and a screen that shows
   * only the name asks a person to approve a destination they were never told.
   */
  destinationLabel: "Onaylarsanız erişim izni şu adrese gönderilir:",
  approve: "Bağla",
  approving: "Bağlanıyor…",
  deny: "Bağlama",
  denying: "İptal ediliyor…",
  leaving: "Asistana geri dönülüyor…",
  /** No request is pending: the person arrived here on their own. */
  idle: {
    title: "Şu anda bağlanacak bir şey yok",
    body: "Asistanınızdan başlayın. Onayınıza ihtiyaç duyduğunda bu sayfayı açar.",
  },
  failure: {
    title: "Bu bağlantı onaylanamaz",
    sign_in_required:
      "Obsina oturumunuz sona erdi. Yeniden giriş yapın, sonra bağlantıyı asistanınızdan başlatın.",
    tenant_selection_required:
      "Hesabınız birden çok kuruma ait. Obsina'da birini seçin, sonra bağlantıyı yeniden başlatın.",
    consent_request_invalid:
      "Bu isteğin süresi doldu ya da yolda değiştirildi. Bağlantıyı asistanınızdan yeniden başlatın.",
    unknown_client:
      "İsteği yapan asistan artık kayıtlı değil. Bağlantıyı asistanınızdan yeniden başlatın.",
    not_authorized:
      "Obsina bir kuruma erişiminizi doğrulayamadı. Üyeliğinizi denetlemesi için bir sahibe başvurun.",
    endpoint_unavailable:
      "Asistan uç noktası bu sayfanın okuyamadığı bir yanıt verdi. Hiçbir şey onaylanmadı.",
    unreachable:
      "Asistan uç noktasına ulaşılamadı. Hiçbir şey onaylanmadı — yeniden deneyin.",
    unpublished:
      "Bu kurulum asistan uç noktası olmadan derlendi; onaylanacak bir şey yok.",
  },
} as const;

export type ConsentMessages = typeof consentMessages;
